import { Link } from "react-router";
import { useAudit } from "../api/queries";
import { tableStyles } from "../components/table";
import { Button, Chip, EmptyState, ErrorState, PageHeader, Skeleton } from "../components/ui";
import { formatDateTime } from "../lib/dates";
import styles from "./pages.module.css";

/** Targets per action: puzzle id, config key, `A<>B` / `from..to` dates, or an email for auth.* */
function Target({ action, target }: { action: string; target: string | null }) {
  if (!target) return <span className={tableStyles.faint}>—</span>;
  let to: string | null = null;
  if (action === "puzzle.status") to = `/puzzles/${encodeURIComponent(target)}`;
  else if (action === "config.set") to = "/config";
  else if (action.startsWith("player.")) to = `/players/${encodeURIComponent(target)}`;
  else if (action.startsWith("schedule.") && /^\d{4}-\d{2}/.test(target)) to = `/schedule?month=${target.slice(0, 7)}`;
  return to ? (
    <Link to={to} className={tableStyles.id}>
      {target}
    </Link>
  ) : (
    <span className={tableStyles.mono}>{target}</span>
  );
}

const ACTION_TONE: Record<string, string> = {
  "puzzle.status": "info",
  "schedule.swap": "violet",
  "schedule.approve": "success",
  "config.set": "warning",
  "player.handle_reset": "danger",
  "auth.login": "muted",
  "auth.logout": "muted",
};

export function AuditPage() {
  const q = useAudit();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <>
      <PageHeader title="Audit log" meta={<span>Every mutating admin call, newest first.</span>} />
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className={tableStyles.wrap}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Admin</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {q.isPending
                  ? Array.from({ length: 10 }, (_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 5 }, (__, j) => (
                          <td key={j}>
                            <Skeleton height={12} width={j === 4 ? 240 : 110} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : items.map((e) => (
                      <tr key={e.id}>
                        <td className={tableStyles.mono}>{formatDateTime(e.createdAt)}</td>
                        <td className={tableStyles.mono}>{e.adminEmail ?? e.adminId ?? "system"}</td>
                        <td>
                          <Chip tone={ACTION_TONE[e.action] ?? "neutral"}>{e.action}</Chip>
                        </td>
                        <td>
                          <Target action={e.action} target={e.target} />
                        </td>
                        <td className={styles.payloadCell}>
                          <code title={JSON.stringify(e.payload, null, 2)}>{JSON.stringify(e.payload)}</code>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
            {!q.isPending && items.length === 0 ? <EmptyState title="Nothing logged yet">Mutations appear here as they happen.</EmptyState> : null}
          </div>
          <div className={tableStyles.footer}>
            <span className="mono">{items.length} entries</span>
            {q.hasNextPage ? (
              <Button size="sm" variant="ghost" busy={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                Load more
              </Button>
            ) : items.length ? (
              <span className="monoLabel">end of log</span>
            ) : null}
          </div>
        </>
      )}
    </>
  );
}

export function NotFoundPage() {
  return (
    <>
      <PageHeader title="Not found" />
      <EmptyState title="Nothing lives at this address" action={<Link to="/">← Dashboard</Link>}>
        Check the URL, or use the sections on the left.
      </EmptyState>
    </>
  );
}
